/* La Caza Cali — Custom PDP behavior
 *
 * Hooks (set in sections/lacaza-product.liquid):
 *   data-pdp-root            wrapper, scope all queries
 *   data-pdp-thumb           gallery thumb buttons
 *   data-pdp-slide           media slides (image/video/model), index-matched to thumbs
 *   data-pdp-variant-media   <script type="application/json"> variant id → media index
 *   data-pdp-fig-current     fig counter span
 *   data-pdp-chip            text variant pill button
 *   data-pdp-variant-id      hidden form input updated on chip click
 *   data-pdp-variants        <script type="application/json"> with full variants array
 *   data-pdp-price           main price display
 *   data-pdp-compare         compare-at price display
 *   data-pdp-cta-price       price suffix inside ATC label
 *   data-pdp-option-value-display   "Finish — Gold" header span
 *   data-pdp-qty-minus / -plus / -input
 *
 * Side effects:
 *   • Pushes URL update on variant change so refresh keeps the variant
 *   • Updates Shopify's `?variant=X` so back button works
 *   • Disables ATC when selected variant is unavailable
 */
(function () {
  'use strict';

  function moneyFormat(cents) {
    // Falls back to en-US dollars without trailing zeros to match the
    // server-rendered Liquid filter (money_without_trailing_zeros).
    var n = Number(cents) / 100;
    var s = n.toFixed(2);
    s = s.replace(/\.00$/, '');
    return '$' + s;
  }

  // Sale tag (same rule as snippets/lacaza-sale-tag.liquid): "Save $X" at $100+, "X% off" below.
  function saleTag(price, compare) {
    if (!compare || !price || compare <= price) return '';
    var diff = compare - price;
    if (price >= 10000) return 'Save $' + Math.floor(diff / 100);
    return Math.round(diff * 100 / compare) + '% off';
  }

  function bindRoot(root) {
    if (!root || root.dataset.pdpBound === '1') return;
    root.dataset.pdpBound = '1';

    // ----- Variants lookup ---------------------------------------
    var variantsEl = root.querySelector('[data-pdp-variants]');
    var variants = [];
    if (variantsEl) {
      try { variants = JSON.parse(variantsEl.textContent || '[]'); }
      catch (e) { variants = []; }
    }

    var variantMediaEl = root.querySelector('[data-pdp-variant-media]');
    var variantMedia = {};
    if (variantMediaEl) { try { variantMedia = JSON.parse(variantMediaEl.textContent || '{}'); } catch (e) {} }

    // ----- Light preview (on/off switch) — variant id -> {on, off, alt} --
    var lightPreviewEl = root.querySelector('[data-pdp-light-preview]');
    var lightPreviewMap = {};
    if (lightPreviewEl) { try { lightPreviewMap = JSON.parse(lightPreviewEl.textContent || '{}'); } catch (e) {} }
    var lightSlide = root.querySelector('[data-pdp-light-slide]');
    var lightThumb = root.querySelector('[data-pdp-light-thumb]');
    var lightThumbImg = root.querySelector('[data-pdp-light-thumb-img]');

    // ----- Secondary light preview (Sept 2026, Glownest) -----------
    // Same pattern as the primary pair above, but keyed off
    // data-pdp-light-preview-2 / -light-slide-2 / -light-thumb-2, always
    // gallery index 1 when present (see sections/lacaza-product.liquid).
    var lightPreviewEl2 = root.querySelector('[data-pdp-light-preview-2]');
    var lightPreviewMap2 = {};
    if (lightPreviewEl2) { try { lightPreviewMap2 = JSON.parse(lightPreviewEl2.textContent || '{}'); } catch (e) {} }
    var lightSlide2 = root.querySelector('[data-pdp-light-slide-2]');
    var lightThumb2 = root.querySelector('[data-pdp-light-thumb-2]');
    var lightThumbImg2 = root.querySelector('[data-pdp-light-thumb-img-2]');

    function applyLightPreview(variant) {
      if (!lightSlide || !lightThumb) return; // product has no pairs at all
      var pair = lightPreviewMap[String(variant.id)];
      if (pair) {
        lightSlide.classList.remove('is-hidden');
        lightThumb.classList.remove('is-hidden');
        if (lightThumbImg) lightThumbImg.src = pair.on;
        if (window.LacazaLightPreview && window.LacazaLightPreview.setPair) {
          var pairWithVariant = { on: pair.on, off: pair.off, alt: pair.alt, variant: variant.id };
          window.LacazaLightPreview.setPair(lightSlide.querySelector('[data-light-preview]'), pairWithVariant);
        }
      } else {
        var wasActive = lightSlide.classList.contains('is-active');
        lightSlide.classList.add('is-hidden');
        lightThumb.classList.add('is-hidden');
        // Light slide is always gallery index 0 when the product has any
        // pair (round 2, Sept 2026) — real media always starts at index 1,
        // so falling back to the first real media means index 1, not 0.
        if (wasActive) showSlide(1);
      }
    }

    function applyLightPreview2(variant) {
      if (!lightSlide2 || !lightThumb2) return; // product has no secondary pair at all
      var pair = lightPreviewMap2[String(variant.id)];
      if (pair) {
        lightSlide2.classList.remove('is-hidden');
        lightThumb2.classList.remove('is-hidden');
        if (lightThumbImg2) lightThumbImg2.src = pair.on;
        if (window.LacazaLightPreview && window.LacazaLightPreview.setPair) {
          var pairWithVariant = { on: pair.on, off: pair.off, alt: pair.alt, variant: variant.id };
          window.LacazaLightPreview.setPair(lightSlide2.querySelector('[data-light-preview]'), pairWithVariant);
        }
      } else {
        var wasActive = lightSlide2.classList.contains('is-active');
        lightSlide2.classList.add('is-hidden');
        lightThumb2.classList.add('is-hidden');
        if (wasActive) showSlide(0);
      }
    }

    var hiddenIdInput = root.querySelector('[data-pdp-variant-id]');
    var priceEl = root.querySelector('[data-pdp-price]');
    var compareEl = root.querySelector('[data-pdp-compare]');
    var saleTagEl = root.querySelector('[data-pdp-sale-tag]');
    var ctaPriceEl = root.querySelector('[data-pdp-cta-price]');
    var atcBtn = root.querySelector('button[name="add"]');
    var atcLabel = root.querySelector('[data-pdp-atc-label]');

    function selectedOptionValues() {
      var chips = root.querySelectorAll('[data-pdp-chip].is-active');
      // Build position-indexed array (1-based positions, 0-indexed array)
      var values = [];
      chips.forEach(function (c) {
        var pos = parseInt(c.dataset.optionPosition, 10) - 1;
        values[pos] = c.dataset.optionValue;
      });
      return values;
    }

    function findVariant(values) {
      return variants.find(function (v) {
        return (v.option1 == values[0] || (!values[0] && !v.option1)) &&
               (v.option2 == values[1] || (!values[1] && !v.option2)) &&
               (v.option3 == values[2] || (!values[2] && !v.option3));
      });
    }

    function applyVariant(variant) {
      if (!variant) return;
      if (hiddenIdInput) hiddenIdInput.value = variant.id;

      // Price + compare
      if (priceEl) priceEl.textContent = moneyFormat(variant.price);
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = moneyFormat(variant.compare_at_price);
          compareEl.removeAttribute('hidden');
        } else {
          compareEl.textContent = '';
          compareEl.setAttribute('hidden', 'hidden');
        }
      }
      if (saleTagEl) {
        var tagText = saleTag(variant.price, variant.compare_at_price);
        saleTagEl.textContent = tagText;
        if (tagText) saleTagEl.removeAttribute('hidden'); else saleTagEl.setAttribute('hidden', 'hidden');
      }

      // ATC label
      if (variant.available) {
        if (atcBtn) atcBtn.removeAttribute('disabled');
        if (atcLabel) {
          atcLabel.innerHTML = 'Add to cart — <span data-pdp-cta-price>' + moneyFormat(variant.price) + '</span>';
        }
      } else {
        if (atcBtn) atcBtn.setAttribute('disabled', 'disabled');
        if (atcLabel) atcLabel.textContent = 'Sold out';
      }

      // Light preview — swap or hide the on/off slide+thumb for this variant.
      applyLightPreview(variant);
      applyLightPreview2(variant);

      // Sticky mobile Add to Cart bar — keep finish/price/availability
      // in sync with whatever variant the shopper just picked.
      if (root._updateStickyAtc) root._updateStickyAtc(variant);

      // Gallery — jump to the variant's featured media slide when mapped.
      var slideIdx = variantMedia[String(variant.id)];
      if (slideIdx !== null && slideIdx !== undefined) showSlide(slideIdx);

      // URL update so refresh / back-button preserve variant.
      // Skip when bound inside the card quick-view modal — that root
      // sits on a collection/other page, and rewriting its URL with a
      // PDP variant id would be surprising and pointless there.
      var inQuickView = !!(root.closest && root.closest('quick-add-modal'));
      if (!inQuickView && variant.id && window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    // ----- Variant chip clicks -----------------------------------
    root.querySelectorAll('[data-pdp-chip]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var pos = chip.dataset.optionPosition;
        var val = chip.dataset.optionValue;

        // Mark this chip active in its option group
        var groupChips = root.querySelectorAll('[data-pdp-chip][data-option-position="' + pos + '"]');
        groupChips.forEach(function (c) {
          var on = (c === chip);
          c.classList.toggle('is-active', on);
          c.setAttribute('aria-checked', on ? 'true' : 'false');
        });

        // Update the "Finish — Gold" header span for this option
        var wrap = chip.closest('[data-pdp-option-wrap]');
        if (wrap) {
          var disp = wrap.querySelector('[data-pdp-option-value-display]');
          if (disp) disp.textContent = val;
        }

        var variant = findVariant(selectedOptionValues());
        applyVariant(variant);
      });
    });

    // ----- Quantity stepper --------------------------------------
    var qtyInput = root.querySelector('[data-pdp-qty-input]');
    var qtyMinus = root.querySelector('[data-pdp-qty-minus]');
    var qtyPlus = root.querySelector('[data-pdp-qty-plus]');
    function clampQty(n) { return Math.max(1, isNaN(n) ? 1 : n); }
    if (qtyMinus && qtyInput) {
      qtyMinus.addEventListener('click', function () {
        qtyInput.value = clampQty(parseInt(qtyInput.value, 10) - 1);
      });
    }
    if (qtyPlus && qtyInput) {
      qtyPlus.addEventListener('click', function () {
        qtyInput.value = clampQty(parseInt(qtyInput.value, 10) + 1);
      });
    }
    if (qtyInput) {
      qtyInput.addEventListener('change', function () {
        qtyInput.value = clampQty(parseInt(qtyInput.value, 10));
      });
    }

    // ----- Gallery slides ----------------------------------------
    var slides = root.querySelectorAll('[data-pdp-slide]');
    var thumbs = root.querySelectorAll('[data-pdp-thumb]');
    var figCurrent = root.querySelector('[data-pdp-fig-current]');

    function showSlide(idx) {
      slides.forEach(function (s) {
        var on = parseInt(s.dataset.pdpSlide, 10) === idx;
        s.classList.toggle('is-active', on);
        var v = s.querySelector('video');
        if (v) {
          if (on) { if (v.play) v.play().catch(function () {}); }
          else { v.pause(); }
        }
      });
      thumbs.forEach(function (t) {
        var on = parseInt(t.dataset.pdpThumb, 10) === idx;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      if (figCurrent) figCurrent.textContent = String(idx + 1).padStart(2, '0');
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        showSlide(parseInt(thumb.dataset.pdpThumb, 10));
      });
    });

    // ----- Sticky mobile Add to Cart bar --------------------------
    // ≤749px only (CSS caps it above that breakpoint). Slides up once
    // the real .lacaza-pdp__atc button scrolls out of view, hides
    // again when it's back in view or the footer is visible.
    var stickyBar = root.querySelector('[data-pdp-sticky-atc]');
    if (stickyBar) {
      var stickyThumb = stickyBar.querySelector('[data-pdp-sticky-thumb]');
      var stickyDot = stickyBar.querySelector('[data-pdp-sticky-dot]');
      var stickyFinish = stickyBar.querySelector('[data-pdp-sticky-finish]');
      var stickyPrice = stickyBar.querySelector('[data-pdp-sticky-price]');
      var stickyCompare = stickyBar.querySelector('[data-pdp-sticky-compare]');
      var stickyBtn = stickyBar.querySelector('[data-pdp-sticky-atc-btn]');
      var stickyLabel = stickyBar.querySelector('[data-pdp-sticky-atc-label]');

      // variant id -> {thumb, hex} — same custom_colors mapping the card
      // swatches use, precomputed server-side (see sections/lacaza-product.liquid).
      var stickyMetaEl = stickyBar.querySelector('[data-pdp-sticky-meta]');
      var stickyMeta = {};
      if (stickyMetaEl) { try { stickyMeta = JSON.parse(stickyMetaEl.textContent || '{}'); } catch (e) {} }

      // Preorder state, read from the same map snippets/lacaza-preorder.liquid
      // builds (variant id -> {q, p, av, sp, pp}). Preorder = a selling plan
      // exists, inventory <= 0, and the variant is otherwise available —
      // identical rule to the snippet's own sync(), kept in lockstep here so
      // the sticky button never disagrees with the real ATC label.
      var preorderDataEl = document.querySelector('[data-lacaza-preorder-data]');
      var preorderMap = null;
      if (preorderDataEl) { try { preorderMap = JSON.parse(preorderDataEl.textContent || '{}'); } catch (e) {} }

      function isPreorderVariant(variant) {
        if (!preorderMap) return false;
        var d = preorderMap[String(variant.id)];
        return !!(d && d.sp && d.q <= 0 && d.av);
      }

      // 900ms "Added" confirmation, only fired once the theme confirms the
      // add actually succeeded (cart:change — dispatched by product-form.js
      // right before it opens the cart drawer). cart:error restores quietly.
      var stickyAddPending = false;
      function showStickyAdded() {
        if (!stickyBtn) return;
        stickyBtn.classList.add('is-added');
        if (stickyLabel) stickyLabel.textContent = 'Added';
        setTimeout(function () {
          stickyBtn.classList.remove('is-added');
          if (stickyLabel) stickyLabel.textContent = stickyBtn.dataset.baseLabel || 'Add to cart';
        }, 900);
      }

      if (stickyBtn) {
        stickyBtn.addEventListener('click', function () {
          // Proxy only — click the REAL submit button so the real form
          // (same variant hidden input, same preorder selling-plan
          // input if STOQ added one) submits through the existing
          // product-form / cart.js handling, cart drawer included.
          if (!atcBtn || atcBtn.disabled || stickyAddPending) return;
          stickyAddPending = true;

          var cleanupTimer;
          function cleanup() {
            document.removeEventListener('cart:change', onSuccess);
            document.removeEventListener('cart:error', onError);
            clearTimeout(cleanupTimer);
            stickyAddPending = false;
          }
          function onSuccess() { cleanup(); showStickyAdded(); }
          function onError() { cleanup(); } // add failed — label was never changed, nothing to restore

          document.addEventListener('cart:change', onSuccess, { once: true });
          document.addEventListener('cart:error', onError, { once: true });
          // Safety net in case neither event fires (e.g. a non-standard
          // cart integration) — just stop waiting, no "Added" flash.
          cleanupTimer = setTimeout(cleanup, 4000);

          atcBtn.click();
        });
      }

      // Visibility is CSS-transform driven (200ms slide), not the
      // [hidden] attribute — that would kill the transition outright.
      // The markup ships hidden for no-JS/pre-hydration safety; once
      // JS is bound, hand visibility over to the .is-visible class.
      stickyBar.removeAttribute('hidden');

      var stickyAtcVisible = true; // real button starts on screen
      var stickyFooterVisible = false;

      // Reserves room at the bottom of the page (bar height + 20px) only
      // while the bar is actually visible, so the floating card never
      // covers footer content. Measured rather than hardcoded so it
      // stays correct if the card ever wraps to a taller layout.
      function syncStickyAtcBodyPad() {
        if (!document.body.classList.contains('has-sticky-atc')) {
          document.body.style.removeProperty('--lacaza-sticky-atc-pad');
          return;
        }
        var h = stickyBar.offsetHeight || 64;
        document.body.style.setProperty('--lacaza-sticky-atc-pad', (h + 20) + 'px');
      }

      function updateStickyAtcVisibility() {
        var show = !stickyAtcVisible && !stickyFooterVisible;
        stickyBar.classList.toggle('is-visible', show);
        // Lets other fixed bottom widgets (chat bubble) push themselves
        // up out of the way — see assets/lacaza-uplift.css.
        document.body.classList.toggle('has-sticky-atc', show);
        syncStickyAtcBodyPad();
      }

      window.addEventListener('resize', function () {
        if (document.body.classList.contains('has-sticky-atc')) syncStickyAtcBodyPad();
      });

      if ('IntersectionObserver' in window && atcBtn) {
        new IntersectionObserver(function (entries) {
          stickyAtcVisible = entries[entries.length - 1].isIntersecting;
          updateStickyAtcVisibility();
        }, { threshold: 0 }).observe(atcBtn);

        // Same footer selector as assets/lacaza-light-mini.js's
        // findFooter() so both bottom widgets agree on "footer visible".
        var footerEl = document.querySelector('footer.lacaza-footer') ||
          document.querySelector('footer[role="contentinfo"]') ||
          document.querySelector('.shopify-section-group-footer-group');
        if (footerEl) {
          new IntersectionObserver(function (entries) {
            stickyFooterVisible = entries[entries.length - 1].isIntersecting;
            updateStickyAtcVisibility();
          }, { threshold: 0 }).observe(footerEl);
        }
      }

      // Called from applyVariant() above on every finish/variant change.
      root._updateStickyAtc = function (variant) {
        var meta = stickyMeta[String(variant.id)];
        if (meta) {
          if (stickyThumb && meta.thumb) stickyThumb.src = meta.thumb;
          if (stickyDot && meta.hex) stickyDot.style.setProperty('--swatch-color', meta.hex);
        }
        if (stickyFinish) stickyFinish.textContent = variant.title;
        if (stickyPrice) stickyPrice.textContent = moneyFormat(variant.price);
        if (stickyCompare) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            stickyCompare.textContent = moneyFormat(variant.compare_at_price);
            stickyCompare.removeAttribute('hidden');
          } else {
            stickyCompare.textContent = '';
            stickyCompare.setAttribute('hidden', 'hidden');
          }
        }
        if (stickyBtn) {
          var baseLabel = 'Add to cart';
          if (!variant.available) {
            baseLabel = 'Sold out';
            stickyBtn.setAttribute('disabled', 'disabled');
          } else {
            stickyBtn.removeAttribute('disabled');
            if (isPreorderVariant(variant)) baseLabel = 'Preorder';
          }
          stickyBtn.dataset.baseLabel = baseLabel;
          // Never stomp the transient "Added" confirmation mid-flash —
          // showStickyAdded() restores from dataset.baseLabel itself.
          if (stickyLabel && !stickyBtn.classList.contains('is-added')) {
            stickyLabel.textContent = baseLabel;
          }
        }
      };
    }

    // ----- Accordion +/− icon swap on toggle ---------------------
    // Native <details> handles open/close; we just need to mirror
    // the open state for the +/− CSS swap (CSS handles the glyph
    // via [open] selector, but we also flip aria-expanded for AT).
    root.querySelectorAll('.lacaza-pdp__acc-item').forEach(function (item) {
      var summary = item.querySelector('summary');
      if (!summary) return;
      summary.setAttribute('aria-expanded', item.hasAttribute('open') ? 'true' : 'false');
      item.addEventListener('toggle', function () {
        summary.setAttribute('aria-expanded', item.hasAttribute('open') ? 'true' : 'false');
      });
    });
  }

  function init() {
    document.querySelectorAll('[data-pdp-root]').forEach(bindRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Exposed so assets/quick-add.js can (re)bind this same logic to the
  // card quick-view modal's content, which is copied in via innerHTML
  // from the fetched product page and never picks up a [data-pdp-root]
  // element for the page-load init() pass above to find.
  window.LacazaPDP = window.LacazaPDP || {};
  window.LacazaPDP.bindRoot = bindRoot;
})();
